/**
 * NFL Week Detection Utility
 *
 * Automatically determines the current NFL week based on the current date.
 * This eliminates the need to manually update default week values across the app.
 *
 * NFL Season Structure:
 * - Regular Season: 18 weeks (early September through early January)
 * - Playoffs: Wild Card, Divisional, Conference Championship, Super Bowl
 * - Off-season: February through August
 *
 * Key Dates (approximate):
 * - Season starts: First Thursday after Labor Day (early September)
 * - Week advances: Every Wednesday after the previous week's games
 * - Regular season ends: Early January
 * - Playoffs: Mid-January through early February
 * - Super Bowl: Second Sunday in February
 */

/**
 * NFL 2024-2025 Season Schedule
 * Week 1 starts: September 5, 2024 (Thursday Night Football)
 * Each week runs Thursday-Monday, advances on Wednesday
 */
const NFL_2024_SEASON_START = new Date('2024-09-05T00:00:00');

/**
 * NFL 2025-2026 Season Schedule (estimated)
 * Week 1 starts: September 4, 2025 (Thursday Night Football)
 */
const NFL_2025_SEASON_START = new Date('2025-09-04T00:00:00');

/**
 * Get the season start date based on the current year
 * @param {number} year - The year to get the season start for
 * @returns {Date} The season start date
 */
const getSeasonStartDate = (year) => {
  // NFL seasons are named by the year they START in
  // E.g., "2024 season" started in September 2024
  if (year === 2024) return NFL_2024_SEASON_START;
  if (year === 2025) return NFL_2025_SEASON_START;

  // For future years, estimate first Thursday after Labor Day
  // Labor Day is first Monday in September
  const laborDay = new Date(year, 8, 1); // September 1
  while (laborDay.getDay() !== 1) {
    laborDay.setDate(laborDay.getDate() + 1);
  }

  // First Thursday after Labor Day
  const seasonStart = new Date(laborDay);
  const daysUntilThursday = (4 - laborDay.getDay() + 7) % 7;
  seasonStart.setDate(laborDay.getDate() + daysUntilThursday);

  return seasonStart;
};

/**
 * Determines the current NFL week based on today's date
 *
 * Logic:
 * 1. Each week runs from Thursday through the following Wednesday
 * 2. Week advances on Wednesday (when most games are complete)
 * 3. Returns appropriate playoff week identifiers
 * 4. Returns null during off-season
 *
 * @param {Date} [currentDate=new Date()] - The date to check (defaults to now)
 * @returns {Object} Object containing weekId, weekNumber, seasonType, seasonYear, and stageName
 */
export const getCurrentNFLWeek = (currentDate = new Date()) => {
  const now = new Date(currentDate);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Determine which season we're in
  // NFL season runs September-February
  // If it's Jan-Jul, we're in the season that started LAST year
  // If it's Aug-Dec, we're in the season that starts THIS year
  let seasonYear;
  if (currentMonth <= 7) {
    seasonYear = currentYear - 1;
  } else {
    seasonYear = currentYear;
  }

  const seasonStart = getSeasonStartDate(seasonYear);

  // Calculate weeks since season start
  const daysSinceSeasonStart = Math.floor((now - seasonStart) / (1000 * 60 * 60 * 24));

  // Off-season detection (before season starts or after Super Bowl)
  if (daysSinceSeasonStart < 0) {
    // Before season starts - show previous season's final week or upcoming Week 1
    if (currentMonth >= 8) {
      // August/early September - show Week 1 of upcoming season
      return {
        weekId: 'week1',
        weekNumber: 1,
        seasonType: 'REG',
        seasonYear: seasonYear,
        stageName: null
      };
    } else {
      // Late off-season (Feb-Jul) - show Super Bowl or Week 1 of current year
      return {
        weekId: 'week1',
        weekNumber: 1,
        seasonType: 'REG',
        seasonYear: currentYear,
        stageName: null
      };
    }
  }

  // Calculate week number
  // Week 1 starts on Thursday (season start)
  // Each week runs Thu-Wed (advances on Wednesday midnight)
  // Simple calculation: divide days by 7 and add 1
  const weekNumber = Math.floor(daysSinceSeasonStart / 7) + 1;

  // Regular season: Weeks 1-18
  if (weekNumber >= 1 && weekNumber <= 18) {
    return {
      weekId: `week${weekNumber}`,
      weekNumber: weekNumber,
      seasonType: 'REG',
      seasonYear: seasonYear,
      stageName: null
    };
  }

  // Playoff weeks (after week 18)
  // Use date-based detection for playoffs since the simple week calculation
  // doesn't align perfectly with the NFL calendar
  //
  // Approximate playoff dates (adjust yearly):
  // - Regular season ends: ~early January (day 122-126)
  // - Wild Card: ~mid-January (day 128-135)
  // - Divisional: ~late January (day 135-142)
  // - Conference: ~late January/early Feb (day 142-150)
  // - Super Bowl: ~early February (day 150-165)

  // Super Bowl week: typically days 150-165 (late Jan through mid-Feb)
  // Check this FIRST since it's the most specific
  if (daysSinceSeasonStart >= 150 && daysSinceSeasonStart <= 165) {
    return {
      weekId: 'superbowl',
      weekNumber: 22,
      seasonType: 'POST',
      seasonYear: seasonYear,
      stageName: 'Super Bowl'
    };
  }

  // Conference Championship: typically days 142-149
  if (daysSinceSeasonStart >= 142 && daysSinceSeasonStart < 150) {
    return {
      weekId: 'conference',
      weekNumber: 21,
      seasonType: 'POST',
      seasonYear: seasonYear,
      stageName: 'Conference Championship'
    };
  }

  // Divisional Round: typically days 135-141
  if (daysSinceSeasonStart >= 135 && daysSinceSeasonStart < 142) {
    return {
      weekId: 'divisional',
      weekNumber: 20,
      seasonType: 'POST',
      seasonYear: seasonYear,
      stageName: 'Divisional'
    };
  }

  // Wild Card: typically days 126-134
  if (daysSinceSeasonStart >= 126 && daysSinceSeasonStart < 135) {
    return {
      weekId: 'wildcard',
      weekNumber: 19,
      seasonType: 'POST',
      seasonYear: seasonYear,
      stageName: 'Wild Card'
    };
  }

  // After Super Bowl (late February onwards) - show Week 1 of next season
  return {
    weekId: 'week1',
    weekNumber: 1,
    seasonType: 'REG',
    seasonYear: seasonYear + 1,
    stageName: null
  };
};

/**
 * Get the current NFL season year
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {number} The season year (e.g., 2024 for the 2024-2025 season)
 */
export const getCurrentNFLSeason = (currentDate = new Date()) => {
  const now = new Date(currentDate);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // If it's Jan-Jul, we're in the season that started last year
  // If it's Aug-Dec, we're in the season that starts this year
  return currentMonth <= 7 ? currentYear - 1 : currentYear;
};

/**
 * Format week info for display
 * @param {Object} weekInfo - Week info from getCurrentNFLWeek()
 * @returns {string} Formatted week label (e.g., "Week 6", "Wild Card")
 */
export const formatNFLWeek = (weekInfo) => {
  if (!weekInfo) return 'Week 1';

  const specialWeeks = {
    'wildcard': 'Wild Card',
    'divisional': 'Divisional',
    'conference': 'Conference Championship',
    'superbowl': 'Super Bowl'
  };

  if (specialWeeks[weekInfo.weekId]) {
    return specialWeeks[weekInfo.weekId];
  }

  return `Week ${weekInfo.weekNumber}`;
};

/**
 * Check if we're currently in the NFL regular season
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {boolean} True if in regular season
 */
export const isNFLRegularSeason = (currentDate = new Date()) => {
  const weekInfo = getCurrentNFLWeek(currentDate);
  return weekInfo.seasonType === 'REG';
};

/**
 * Check if we're currently in the NFL playoffs
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {boolean} True if in playoffs
 */
export const isNFLPlayoffs = (currentDate = new Date()) => {
  const weekInfo = getCurrentNFLWeek(currentDate);
  return weekInfo.seasonType === 'POST';
};

export default {
  getCurrentNFLWeek,
  getCurrentNFLSeason,
  formatNFLWeek,
  isNFLRegularSeason,
  isNFLPlayoffs
};
